import ProtoBufService from './ProtoBufService'
import * as LiveControlPB from './libs/messaging/live_control_pb'

export namespace LiveControl {
  export enum TorchLevel {
    DISABLED = 0,
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3,
  }

  export enum Resolution {
    AUTO = 0,
    PRESET_1 = 1,
    PRESET_2 = 2,
    PRESET_3 = 3,
    PRESET_4 = 4,
  }

  export enum FocusMode {
    AUTO = 0,
    MANUAL = 1,
  }

  export enum LogType {
    ANY = 0,
    LIVE = 1,
  }

  export  class LiveControlService extends ProtoBufService {
    constructor () {
      super()
      this.service = ProtoBufService.ServiceType.LIVE_CONTROL
    }

    pushToTalk(id: number, active: boolean) {
      const payload = new LiveControlPB.PushToTalkControl.PushToTalkState()
      payload.setActive(active);
      return this.request(id, LiveControlPB.LiveControls.PUSH_TO_TALK, payload);
    }

    record(id: number, recording: boolean) {
      const payload = new LiveControlPB.ManualRecordingControl()
      payload.setRecording(recording);
      return this.request(id, LiveControlPB.LiveControls.RECORDING_CONTROL, payload);
    }

    requestRecordingResult(id: number) {
      const payload = new LiveControlPB.ManualRecordingResultRequest()
      return this.request(id, LiveControlPB.LiveControls.RECORDING_RESULT, payload)
    }

    setTorch(id: number, level: TorchLevel) {
      const payload = new LiveControlPB.TorchControl.TorchState()
      switch (level) {
        case TorchLevel.DISABLED:
          payload.setLevel(LiveControlPB.TorchControl.TorchState.TorchLevel.DISABLED)
          break
        case TorchLevel.LOW:
          payload.setLevel(LiveControlPB.TorchControl.TorchState.TorchLevel.LOW)
          break
        case TorchLevel.MEDIUM:
          payload.setLevel(LiveControlPB.TorchControl.TorchState.TorchLevel.MEDIUM)
          break
        case TorchLevel.HIGH:
          payload.setLevel(LiveControlPB.TorchControl.TorchState.TorchLevel.HIGH)
          break
      }
      return this.request(id, LiveControlPB.LiveControls.TORCH, payload)
    }

    setOrientation(id: number, degree: number) {
      const payload = new LiveControlPB.OrientationControl.OrientationState()
      payload.setDegree(degree);
      return this.request(id, LiveControlPB.LiveControls.ORIENTATION, payload)
    }

    setSiren(id: number, enabled: boolean) {
      const payload = new LiveControlPB.SirenControl.SirenState()
      payload.setEnabled(enabled);
      return this.request(id, LiveControlPB.LiveControls.SIREN, payload);
    }

    setResolution(id: number, resolution: Resolution) {
      const payload = new LiveControlPB.ResolutionControl();
      switch (resolution) {
        case Resolution.AUTO:
          payload.setState(LiveControlPB.ResolutionControl.ResolutionState.AUTO)
          break
        case Resolution.PRESET_1:
          payload.setState(LiveControlPB.ResolutionControl.ResolutionState.PRESET_1)
          break
        case Resolution.PRESET_2:
          payload.setState(LiveControlPB.ResolutionControl.ResolutionState.PRESET_2)
          break
        case Resolution.PRESET_3:
          payload.setState(LiveControlPB.ResolutionControl.ResolutionState.PRESET_3)
          break
        case Resolution.PRESET_4:
          payload.setState(LiveControlPB.ResolutionControl.ResolutionState.PRESET_4)
          break
      }
      return this.request(id, LiveControlPB.LiveControls.RESOLUTION, payload)
    }

    zoom(id: number, zoomIn: boolean, x: number, y: number) {
      const payload = new LiveControlPB.ZoomControl()
      const state = new LiveControlPB.ZoomControl.ZoomState()
      state.setZoomIn(zoomIn)
      state.setScale(0)
      state.setCoordX(x)
      state.setCoordY(y)
      payload.setState(state)
      payload.setAnimate(true)
      return this.request(id, LiveControlPB.LiveControls.ZOOM, payload)
    }

    focus(id: number, mode: FocusMode, x: number, y: number) {
      const payload = new LiveControlPB.FocusControl()
      const state = new LiveControlPB.FocusControl.FocusState()
      switch (mode) {
        case FocusMode.AUTO:
          state.setMode(LiveControlPB.FocusControl.FocusState.FocusMode.AUTO)
          break
        case FocusMode.MANUAL:
          state.setMode(LiveControlPB.FocusControl.FocusState.FocusMode.MANUAL)
          break
        default:
          break
      }
      state.setCoordX(x)
      state.setCoordY(y)
      return this.request(id, LiveControlPB.LiveControls.FOCUS, payload)
    }

    uploadLog(id: number, type: LogType, url: string) {
      const payload = new LiveControlPB.LogUploadRequest()
      switch (type) {
        case LogType.ANY:
          payload.setType(LiveControlPB.LogUploadRequest.LogType.ANY)
          break
        case LogType.LIVE:
          payload.setType(LiveControlPB.LogUploadRequest.LogType.LIVE)
          break
        default:
          break
      }
      payload.setUploadUrl(url)
      return this.request(id, LiveControlPB.LiveControls.LOG_UPLOAD, payload)
    }

    onRequest({ control, payload }: { control: number, payload: Uint8Array }) {
      let data
      switch (control) {
        case LiveControlPB.LiveControls.PUSH_TO_TALK:
          data = LiveControlPB.PushToTalkControl.deserializeBinary(payload)
          return {
            type: 'pushToTalk',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.RECORDING_CONTROL:
          data = LiveControlPB.ManualRecordingControl.deserializeBinary(payload)
          return {
            type: 'recordingControl',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.RECORDING_RESULT:
          data = LiveControlPB.ManualRecordingResultRequest.deserializeBinary(payload)
          return {
            type: 'recordingResult',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.TORCH:
          data = LiveControlPB.TorchControl.TorchState.deserializeBinary(payload)
          return {
            type: 'torch',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.ORIENTATION:
          data = LiveControlPB.OrientationControl.OrientationState.deserializeBinary(payload)
          return {
            type: 'orientation',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.SIREN:
          data = LiveControlPB.SirenControl.SirenState.deserializeBinary(payload)
          return {
            type: 'siren',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.RESOLUTION:
          data = LiveControlPB.ResolutionControl.deserializeBinary(payload)
          return {
            type: 'resolution',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.ZOOM:
          data = LiveControlPB.ZoomControl.deserializeBinary(payload)
          return {
            type: 'zoom',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.FOCUS:
          data = LiveControlPB.FocusControl.deserializeBinary(payload)
          return {
            type: 'focus',
            data: data.toObject(),
          }
        case LiveControlPB.LiveControls.LOG_UPLOAD:
          data = LiveControlPB.LogUploadRequest.deserializeBinary(payload)
          return {
            type: 'logUpload',
            data: data.toObject(),
          }
        default:
            break;
      }
      return null
    }

    onResponse({ control, payload }: { control: number, payload: Uint8Array }) {
      switch (control) {
        case LiveControlPB.LiveControls.PUSH_TO_TALK:
          return {
            type: 'pushToTalk',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.RECORDING_CONTROL:
          return {
            type: 'recordingControl',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.RECORDING_RESULT:
          const manualRecordingResultResponse = LiveControlPB.ManualRecordingResultResponse.deserializeBinary(payload)
          return {
            type: 'recordingResult',
            data: manualRecordingResultResponse.toObject()
          }
        case LiveControlPB.LiveControls.TORCH:
          return {
            type: 'torch',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.ORIENTATION:
          return {
            type: 'orientation',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.SIREN:
          return {
            type: 'siren',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.RESOLUTION:
          return {
            type: 'resolution',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.ZOOM:
          return {
            type: 'zoom',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.FOCUS:
          return {
            type: 'focus',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case LiveControlPB.LiveControls.LOG_UPLOAD:
          return {
            type: 'logUpload',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        default:
            break;
      }
      return null
    }
  }
}